import { Checkbox, EmptyState } from '@procurify/ui'

import { StatusCardLegacyLoader } from '@webapp/core/components/StatusCardLegacy/StatusCardLegacyLoader'
import { StatusCardLegacyList } from '@webapp/core/components/StatusCardLegacyList'
import { useProcurifyIntl } from '@webapp/hooks'
import { ApproverChoiceSelect } from '@webapp/modules/expense/components/ApproverChoiceSelect'
import { TravelCard } from '@webapp/modules/expense/components/TravelCard'
import { type TravelRequestSummary } from '@webapp/modules/expense/entities/TravelRequestSummary'
import messages from './messages'

export interface IApprovableTravelCardsProps {
  travels: TravelRequestSummary[]
  isLoading: boolean
  /** Pending tab && no watchlist filter (computed by the Feature Set). */
  isSelectable: boolean
  selectedIds: Set<number>
  approverIdByTravelId: Record<string, Nullable<number>>
  justUpdatedId?: number
  onToggleSelect: (travel: TravelRequestSummary, checked: boolean) => void
  onApproverPick: (travelId: number, approverId: Nullable<number>) => void
}

export const ApprovableTravelCards = ({
  travels,
  isLoading,
  isSelectable,
  selectedIds,
  approverIdByTravelId,
  justUpdatedId,
  onToggleSelect,
  onApproverPick,
}: IApprovableTravelCardsProps) => {
  const intl = useProcurifyIntl()

  const showEmptyState = !isLoading && travels.length === 0

  return (
    <StatusCardLegacyList>
      {isLoading
        ? new Array(5).fill(null).map((_, index) => (
            <StatusCardLegacyList.Item key={index}>
              <StatusCardLegacyLoader />
            </StatusCardLegacyList.Item>
          ))
        : travels.map((travel) => (
            <StatusCardLegacyList.Item key={travel.id}>
              <TravelCard
                travel={travel}
                isJustUpdated={travel.id === justUpdatedId}
                checkboxSlot={
                  isSelectable && travel.isPending ? (
                    <Checkbox
                      checked={selectedIds.has(travel.id)}
                      onChange={(e) => onToggleSelect(travel, e.target.checked)}
                      inputProps={{
                        'aria-label': intl.formatMessage(
                          messages.selectTravelAriaLabel,
                          { travelId: travel.id }
                        ),
                      }}
                    />
                  ) : undefined
                }
                nextApproverSlot={
                  isSelectable &&
                  travel.isPending &&
                  travel.nextApproverChoices.length > 0 ? (
                    <ApproverChoiceSelect
                      choices={travel.nextApproverChoices}
                      selectedApproverId={
                        approverIdByTravelId[String(travel.id)] ?? null
                      }
                      onChange={(approverId) =>
                        onApproverPick(travel.id, approverId)
                      }
                    />
                  ) : undefined
                }
              />
            </StatusCardLegacyList.Item>
          ))}

      {showEmptyState ? (
        <EmptyState header={intl.formatMessage(messages.emptyStateHeader)} />
      ) : null}
    </StatusCardLegacyList>
  )
}

ApprovableTravelCards.displayName = 'ApprovableTravelCards'
